/**
 * Менеджер тегов: переименовать, удалить, завести и править автотег. Словарь общий на всех (тег — про
 * картинку, а не про чью-то папку), поэтому правит любой вошедший; кто и когда завёл тег — видно в таблице.
 *
 * Строки сгруппированы: заголовок группы автотегов («Силуэт»), под ним её теги; теги без группы — в конце.
 * У автотега — «авто N · ручных M · совпало K из M» (совпало — ручные метки, где автотег согласен с человеком)
 * и начало промпта; «Промпт…» открывает редактор (`AutotagEditor`), у ручного тега — делает его автотегом.
 * Сверху — «+ Автотег», «Переразметить все» (итог по тегам) и число фото без вектора: их автотеги не видят
 * до `library embed`.
 *
 * Удаление снимает тег со всех пинов и референсов без возврата (истории у тегов нет), так
 * что подтверждение называет число пинов, ручных меток и автометок — единственный предохранитель.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, Trash2 } from "lucide-react";
import { Fragment, useState, type FormEvent } from "react";

import { AutotagEditor } from "@/components/library/AutotagEditor";
import { Dialog } from "@/components/trends/Dialog";
import { btn, btnGhost, btnPrimary, Empty, ErrorLine, input, Loading } from "@/components/trends/ui";
import { reportLine } from "@/lib/autotag";
import { plural } from "@/lib/library";
import { num, when } from "@/lib/media";
import { deleteTag, loadTags, renameTag, runAutotags, tagKeys, tagStyle, type Tag } from "@/lib/tags";

export function TagManager({
  open,
  onClose,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  /** Тег переименован, удалён или переразмечен — владелец перезапрашивает экран. */
  onChanged: () => void;
}) {
  return (
    <Dialog open={open} wide title="Теги" onClose={onClose}>
      {open ? <TagTable onChanged={onChanged} /> : null}
    </Dialog>
  );
}

const isAutoTag = (t: Tag) => t.prompt_en !== null;

/** Группы по имени, теги без группы — последним блоком (`name` = null). */
function byGroup(tags: Tag[]): { name: string | null; tags: Tag[] }[] {
  const groups = new Map<string, Tag[]>();
  const loose: Tag[] = [];
  for (const t of tags) {
    if (t.group_name === null) loose.push(t);
    else groups.set(t.group_name, [...(groups.get(t.group_name) ?? []), t]);
  }
  const out: { name: string | null; tags: Tag[] }[] = [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b, "ru"))
    .map(([name, list]) => ({ name, tags: list }));
  if (loose.length) out.push({ name: null, tags: loose });
  return out;
}

/** Счётчики меток строки: у автотега — авто/ручные/совпало, у ручного — пины и референсы, как раньше. */
function marksText(t: Tag): string {
  const pins = t.pins ? `пинов ${num(t.pins)} · ` : "";
  if (!isAutoTag(t) && !t.auto) return `пинов ${num(t.pins)} · референсов ${num(t.items)}`;
  const agree = t.agree !== null && t.manual > 0 ? ` · совпало ${num(t.agree)} из ${num(t.manual)}` : "";
  return `${pins}авто ${num(t.auto)} · ручных ${num(t.manual)}${agree}`;
}

function TagTable({ onChanged }: { onChanged: () => void }) {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: tagKeys.list, queryFn: loadTags });
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  // Редактор автотега: `{tag: null}` — новый.
  const [prompting, setPrompting] = useState<{ tag: Tag | null } | null>(null);
  const [notice, setNotice] = useState<string[]>([]);

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: tagKeys.list });
    onChanged();
  };
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => renameTag(id, name),
    onSuccess: () => {
      setEditing(null);
      done();
    },
  });
  const remove = useMutation({
    mutationFn: (id: number) => deleteTag(id),
    onSuccess: (got) => {
      setNotice(got.rerun_error ? [`Тег удалён, но остальные теги группы не переразмечены: ${got.rerun_error}`] : []);
      done();
    },
  });
  const rerun = useMutation({
    mutationFn: runAutotags,
    onMutate: () => setNotice([]),
    onSuccess: (got) => {
      setNotice(got.report.length ? got.report.map((r) => `«${r.name}»: ${reportLine(r)}`) : ["Автотегов пока нет."]);
      done();
    },
  });

  const start = (t: Tag) => {
    rename.reset();
    setEditing(t.id);
    setDraft(t.name);
  };
  const save = (e: FormEvent, t: Tag) => {
    e.preventDefault();
    const name = draft.trim();
    if (!name || name === t.name) return setEditing(null);
    rename.mutate({ id: t.id, name });
  };
  const drop = (t: Tag) => {
    const pins = `${num(t.pins)} ${plural(t.pins, "пина", "пинов", "пинов")}`;
    const refs =
      isAutoTag(t) || t.auto
        ? `референсов: ручных меток ${num(t.manual)}, автометок ${num(t.auto)}`
        : `${num(t.items)} ${plural(t.items, "референса", "референсов", "референсов")}`;
    if (window.confirm(`Удалить тег «${t.name}»? Он снят с ${pins} и ${refs}.`)) remove.mutate(t.id);
  };

  if (list.isPending) return <Loading />;
  if (list.isError)
    return (
      <div className="p-4">
        <ErrorLine error={list.error} />
      </div>
    );
  const { tags, autotag_pending: pending } = list.data;
  const sections = byGroup(tags);
  const hasGroups = sections.some((s) => s.name !== null);
  const hasAuto = tags.some(isAutoTag);

  return (
    <div className="grid content-start gap-2 p-3" data-annot="менеджер тегов">
      <div className="flex flex-wrap items-center gap-2" data-annot="действия тегов">
        <button type="button" className={btnPrimary} onClick={() => setPrompting({ tag: null })}>
          <Plus size={13} />
          Автотег
        </button>
        <button
          type="button"
          className={btn}
          onClick={() => rerun.mutate()}
          disabled={rerun.isPending || !hasAuto}
          title={hasAuto ? "Заново разметить все фото всеми автотегами (бесплатно)" : "Автотегов пока нет"}
        >
          <RefreshCw size={13} className={rerun.isPending ? "animate-spin" : undefined} />
          {rerun.isPending ? "Размечаю…" : "Переразметить все"}
        </button>
        {pending > 0 ? (
          <span className="text-[12px] text-fg-faint tnum" title="Фото, для которых ещё не посчитан вектор картинки">
            без вектора {num(pending)} — разметятся после <code>library embed</code>
          </span>
        ) : null}
      </div>
      {notice.length ? (
        <div
          className="grid gap-0.5 rounded-[var(--radius-ui)] bg-bg-subtle px-3 py-2 text-[12.5px] tnum"
          data-annot="итог разметки"
        >
          {notice.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
      ) : null}
      <ErrorLine error={rerun.error ?? remove.error} />

      {!tags.length ? (
        <Empty>Тегов пока нет — их заводят из панели выделения пинов и референсов или кнопкой «+ Автотег».</Empty>
      ) : (
        <>
          <div className="text-[11.5px] text-fg-faint">
            Клик по названию — переименовать (Enter — сохранить, Esc — отмена). «Промпт…» — отбирать фото в тег
            автоматически, по описанию.
          </div>
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="text-[11px] text-fg-muted">
                <th className="border-b border-border px-1.5 py-1 text-left">тег</th>
                <th className="border-b border-border px-1.5 py-1 text-left">метки</th>
                <th className="border-b border-border px-1.5 py-1 text-left">промпт</th>
                <th className="border-b border-border px-1.5 py-1 text-left">завёл</th>
                <th className="border-b border-border" />
              </tr>
            </thead>
            <tbody>
              {sections.map((s) => (
                <Fragment key={s.name ?? "\u0000loose"}>
                  {hasGroups ? (
                    <tr>
                      <th
                        colSpan={5}
                        className="border-b border-border bg-bg-subtle px-1.5 py-1 text-left text-[12px] font-medium text-fg"
                      >
                        {s.name ?? "без группы"}
                      </th>
                    </tr>
                  ) : null}
                  {s.tags.map((t) => (
                    <tr key={t.id} className="align-top">
                      <td className="w-[22%] border-b border-border px-1.5 py-1">
                        {editing === t.id ? (
                          <form onSubmit={(e) => save(e, t)} className="grid gap-1">
                            <input
                              autoFocus
                              className={`${input} w-full`}
                              value={draft}
                              maxLength={60}
                              onChange={(e) => setDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key !== "Escape") return;
                                // Esc в поле — отмена правки, а не закрытие окна.
                                e.preventDefault();
                                e.stopPropagation();
                                setEditing(null);
                              }}
                              onBlur={() => (rename.isPending ? undefined : setEditing(null))}
                              disabled={rename.isPending}
                            />
                            <ErrorLine error={rename.error} />
                          </form>
                        ) : (
                          <button
                            type="button"
                            onClick={() => start(t)}
                            className="max-w-full truncate rounded-[3px] px-1 text-left text-[12px]"
                            style={tagStyle(t.id)}
                            title="Переименовать"
                          >
                            {t.name}
                          </button>
                        )}
                      </td>
                      <td className="border-b border-border px-1.5 py-1 whitespace-nowrap text-fg-muted tnum">
                        {marksText(t)}
                        {t.removed ? (
                          <span className="text-fg-faint" title="Автометки, снятые вручную: пересчёт их не вернёт">
                            {" "}
                            · снято вручную {num(t.removed)}
                          </span>
                        ) : null}
                      </td>
                      <td className="max-w-[320px] border-b border-border px-1.5 py-1">
                        {t.prompt ? (
                          <span
                            className="block truncate text-fg-muted"
                            title={`${t.prompt}${t.prompt_en ? `\n— для модели: ${t.prompt_en}` : ""}`}
                          >
                            {t.prompt.split("\n")[0]}
                          </span>
                        ) : (
                          <span className="text-fg-faint">ручной</span>
                        )}
                      </td>
                      <td className="border-b border-border px-1.5 py-1 whitespace-nowrap text-fg-muted">
                        {t.author_name} · <span className="tnum">{when(t.date_create)}</span>
                      </td>
                      <td className="border-b border-border px-1 py-0.5 whitespace-nowrap">
                        <button
                          type="button"
                          className={btnGhost}
                          onClick={() => setPrompting({ tag: t })}
                          title={
                            isAutoTag(t)
                              ? "Править промпт, группу и порог"
                              : "Сделать автотегом: отбирать фото по описанию"
                          }
                        >
                          Промпт…
                        </button>
                        <button
                          type="button"
                          className={btnGhost}
                          onClick={() => drop(t)}
                          disabled={remove.isPending}
                          title="Удалить тег"
                          aria-label={`Удалить тег ${t.name}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </>
      )}

      <AutotagEditor
        tag={prompting?.tag ?? null}
        open={prompting !== null}
        onClose={() => setPrompting(null)}
        onSaved={(line) => {
          setPrompting(null);
          setNotice([line]);
          done();
        }}
      />
    </div>
  );
}
